import { Country } from "@utils/constants/localisation";
import { DocumentDetail } from "@views/IdentityVerificationDocumentUpload/documentDetailReducer";

export enum IdentityDocumentType {
    DRIVER_LICENCE = 'Driver Licence',
    PASSPORT = 'Passport',
    MEDICARE_CARD = 'Medicare Card',
    CREDIT_CARD = 'Credit or Debit Card',
    PCO = 'PCO',
    BANK_STATEMENT = 'Bank Statement',
    UTILITY_BILL = 'Utility Bill'
}

/* Allows for configuring documents differently based on country, when some document types share common fields */
interface BaseDocumentConfig {
    includedFields: (keyof DocumentDetail)[];
    requiredFields: (keyof DocumentDetail)[];
    requiresThreeMonthExpiry: boolean;

    // Country-specific overrides for fields which aren't shared between countries
    countryOverrides?: {
        [key in Country]?: {
            additionalIncluded?: (keyof DocumentDetail)[];
            additionalRequired?: (keyof DocumentDetail)[];
        }
    };
}

export interface DocumentConfig {
    // List of all the fields for a given document type.
    // Each includedField is the name of a field in the reducer's DocumentDetail.
    // There should be a matching input in DocumentPanel for each one.
    includedFields: (keyof DocumentDetail)[];

    // List of required fields for a given document type.
    // Used for validation to ensure all necessary fields are filled.
    requiredFields: (keyof DocumentDetail)[];

    // Must the expiry date for this document be 3 months in the future?
    requiresThreeMonthExpiry: boolean;
}

const PROOF_OF_ADDRESS_CONFIG: BaseDocumentConfig = {
    includedFields: ['documentType', 'issueDate', 'streetLine1', 'streetLine2', 'city', 'postCode', 'frontFile'],
    requiredFields: ['documentType', 'issueDate', 'streetLine1', 'city', 'postCode', 'frontFile'], // streetLine2 is optional
    requiresThreeMonthExpiry: false, // no expiry date for Proof of Address documents
};

const DOCUMENT_CONFIG: Record<IdentityDocumentType, BaseDocumentConfig> = {
    [IdentityDocumentType.DRIVER_LICENCE]: {
        // documentType is included, but it'll be automatically set and not show the dropdown in this case.
        includedFields: ['documentType', 'documentNumber', 'expiryDate', 'frontFile', 'backFile'],
        requiredFields: ['documentType', 'documentNumber', 'expiryDate', 'frontFile', 'backFile'],
        requiresThreeMonthExpiry: false,
        countryOverrides: {
            [Country.AU]: {
                additionalIncluded: ['dateOfBirth', 'state'],
                additionalRequired: ['dateOfBirth', 'state'],
            },
        }
    },
    [IdentityDocumentType.PASSPORT]: {
        includedFields: ['documentType', 'documentNumber', 'expiryDate', 'nationality', 'frontFile'],
        requiredFields: ['documentType', 'documentNumber', 'expiryDate', 'nationality', 'frontFile'],
        requiresThreeMonthExpiry: true
    },
    [IdentityDocumentType.MEDICARE_CARD]: {
        includedFields: ['documentType', 'expiryDate', 'frontFile'],
        requiredFields: ['documentType', 'expiryDate', 'frontFile'],
        requiresThreeMonthExpiry: false
    },
    [IdentityDocumentType.CREDIT_CARD]: {
        includedFields: ['documentType', 'expiryDate'],
        requiredFields: ['documentType', 'expiryDate'],
        requiresThreeMonthExpiry: false
    },
    [IdentityDocumentType.PCO]: {
        includedFields: ['documentType', 'documentNumber', 'expiryDate', 'frontFile', 'backFile'],
        requiredFields: ['documentType', 'documentNumber', 'expiryDate', 'frontFile', 'backFile'],
        requiresThreeMonthExpiry: false
    },
    [IdentityDocumentType.BANK_STATEMENT]: PROOF_OF_ADDRESS_CONFIG,
    [IdentityDocumentType.UTILITY_BILL]: PROOF_OF_ADDRESS_CONFIG,
};

/**
 * Resolves the document config for a given document type and country,
 * merging any country-specific overrides into the base config.
 */
export function getDocumentConfig(
    documentType: IdentityDocumentType,
    country: Country
): DocumentConfig {
    const baseConfig = DOCUMENT_CONFIG[documentType];
    const countryOverride = baseConfig.countryOverrides?.[country];

    if (!countryOverride) {
        return {
            includedFields: baseConfig.includedFields,
            requiredFields: baseConfig.requiredFields,
            requiresThreeMonthExpiry: baseConfig.requiresThreeMonthExpiry,
        };
    }

    return {
        includedFields: [
            ...baseConfig.includedFields,
            ...(countryOverride.additionalIncluded ?? [])
        ],
        requiredFields: [
            ...baseConfig.requiredFields,
            ...(countryOverride.additionalRequired ?? [])
        ],
        requiresThreeMonthExpiry: baseConfig.requiresThreeMonthExpiry,
    };
}

export class IdentityVerificationDocument {
    constructor(
        // Common document attributes
        public documentType: IdentityDocumentType,
        public documentNumber?: string,
        public expiryDate?: string,

        // Specific to drivers licence or passport
        public dateOfBirth?: string,
        public state?: string,
        public nationality?: string,

        // Proof of Address
        public issueDate?: string,
        public streetLine1?: string,
        public streetLine2?: string,
        public city?: string,
        public postCode?: string,
        public country?: string,

        // Files to upload, if any
        public frontFileName?: string,
        public backFileName?: string
    ) { }

    static fromDocumentDetail(detail: DocumentDetail): IdentityVerificationDocument {
        return new IdentityVerificationDocument(
            detail.documentType!, // validated to be non-null by the time we call this
            detail.documentNumber || undefined,
            detail.expiryDate || undefined,
            detail.dateOfBirth || undefined,
            detail.state || undefined,
            detail.nationality || undefined,
            detail.issueDate || undefined,
            detail.streetLine1 || undefined,
            detail.streetLine2 || undefined,
            detail.city || undefined,
            detail.postCode || undefined,
            detail.country || undefined,
            detail.frontFile?.name,
            detail.backFile?.name
        );
    }

    toJSON(): object {
        const additionalData = {
            ...(this.dateOfBirth && { DateOfBirth: this.dateOfBirth }),
            ...(this.state && { State: this.state }),
            ...(this.nationality && { Nationality: this.nationality }),
            ...(this.issueDate && { IssueDate: this.issueDate }),
            ...(this.streetLine1 && {
                AddressLines: [
                    this.streetLine1,
                    ...(this.streetLine2 ? [this.streetLine2] : [])
                ]
            }),
            ...(this.city && { Suburb: this.city }),
            ...(this.postCode && { PostCode: this.postCode }),
            ...(this.country && { Country: this.country }),
        };

        return {
            document_type: this.documentType,
            ...(this.documentNumber && { document_number: this.documentNumber }),
            ...(this.expiryDate && { expiry_date: this.expiryDate }),
            ...(this.frontFileName && { front_file_name: this.frontFileName }),
            ...(this.backFileName && { back_file_name: this.backFileName }),

            // Only include additional_data if it has properties
            ...(Object.keys(additionalData).length > 0 && { additional_data: additionalData })
        };
    }
}

export class ManualUploadRequest {
    constructor(
        public country: string,
        public zohoContactId: string,
        public documents: IdentityVerificationDocument[],
    ) { }

    toJSON(): object {
        return {
            country: this.country,
            zoho_contact_id: this.zohoContactId,
            documents: this.documents.map(doc => doc.toJSON()),
        };
    }
}

export interface IDocumentUploadUrls {
    front_file?: {
        file_name: string;
        description: string;
        presigned_upload_url: string;
    } | null;
    back_file?: {
        file_name: string;
        description: string;
        presigned_upload_url: string;
    } | null;
}

export interface IManualUploadResponse {
    reference: string;
    presigned_upload_urls: IDocumentUploadUrls[];
}

export class FinaliseManualUploadRequest {
    constructor(
        public success: boolean,
        public nationalInsuranceNumber?: string,
    ) { }

    toJSON(): object {
        return {
            success: this.success,
            ...(this.nationalInsuranceNumber && {
                additional_zoho_fields: { national_insurance_number: this.nationalInsuranceNumber }
            })
        };
    }
}

export interface IFinaliseManualUploadResponse {
    reference: string;
}

/**
 * Request to add a single document to an existing identity verification check
 */
export class AddDocumentRequest {
    constructor(
        public country: string,
        public zohoContactId: string,
        public document: IdentityVerificationDocument
    ) { }

    toJSON(): object {
        return {
            country: this.country,
            zoho_contact_id: this.zohoContactId,
            ...this.document.toJSON()
        };
    }
}

/**
 * Response from adding a document to an existing identity verification check
 */
export interface IAddDocumentResponse {
    identity_verification_check_reference_id: string;
    presigned_upload_urls: {
        front_file: {
            file_name: string;
            description: string;
            presigned_upload_url: string;
        } | null;
        back_file: {
            file_name: string;
            description: string;
            presigned_upload_url: string;
        } | null;
    };
}
